import { memo, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import {
  Layout,
  DataType,
  ComponentType,
} from '@vgtv/api-client/lib/component';

import { FilteredList } from '../../../FilteredList/FilteredList';
import { config } from '../../../../config';
import {
  getAssetLabels,
  getAssetMainImage,
  getAssetVerticalImage,
  isAssetRestricted,
} from '../../../../shared/helpers/assetHelpers';
import { fetchSvpAssets } from '../../../../modules/assets';
import { stripTags } from '../../../../utils/string';
import { getImageSources } from '../../../../helpers/images';
import { pickRandomArrayElement } from '../../../../utils/pickRandomArrayElement';
import { getComponentDataFetchLimit } from '../../getComponentDataFetchLimit';
import { useViewportType } from '../../../utils/useViewportType';
import { getAssetProps } from '../../../../helpers/getAssetProps';
import { getCurrentPageViewData } from '../../../../services/tracking/helpers/pulseOrigin';
import { trackClickEvent } from '../../../../services/tracking/pulseEvents';
import { useAppDispatch } from '../../../../modules/hooks';
import { useVendor } from '../../../VendorPovider/VendorPovider';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { FilteredListComponent } from '@vgtv/api-client/lib/component/filtered_list';
import type { FC } from 'react';

import * as styles from './AssetsFilteredList.module.scss';

interface AssetsFilteredProps {
  componentConfig: Pick<
    FilteredListComponent,
    'header' | 'filters' | 'categoryId' | 'options' | 'type'
  >;
  searchInputRef: React.Ref<HTMLInputElement>;
  pageComponentIndex?: number;
}

const AssetsFilteredListBase: FC<AssetsFilteredProps> = ({
  componentConfig,
  searchInputRef,
  pageComponentIndex,
}) => {
  const { header, filters = [], categoryId, options } = componentConfig;
  const { vendor } = useVendor();
  const [assets, setAssets] = useState<SvpAssetAdditional[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();
  const { viewportType } = useViewportType();
  const { useVerticalPosters, placeholder, hideSearchField } = options || {};

  // only pick the random item once - otherwise it would change on every render causing an infinite loop
  const initiallyActiveFilter = useMemo(
    () => pickRandomArrayElement(filters),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onTrackSearch = useCallback(
    (query) => {
      const parent = {
        elementType:
          componentConfig.type === ComponentType.FILTERED_LIST
            ? 'FilteredList'
            : 'SearchPopup',
        name: 'Search',
      };

      const object = {
        title: query,
        elementType: 'SearchForm',
        id: 'search-form',
      };
      const currentPageViewData = getCurrentPageViewData();

      const target = {
        type: 'AssetsFilteredList',
        title: query,
        url: currentPageViewData.url,
      };

      trackClickEvent(parent, object, target);
    },
    [componentConfig.type]
  );

  const onFiltersChange = useCallback(
    async function ({ query, tagId, storyId }) {
      const hasQuery = Boolean(query);
      const hasTagFilters = Boolean(tagId);
      const hasStoryFilters = Boolean(storyId);
      const hasCategoryFilter = Boolean(categoryId);

      if (hasQuery || hasTagFilters || hasStoryFilters) {
        const filters = [];
        if (hasTagFilters || hasStoryFilters || hasCategoryFilter) {
          if (hasCategoryFilter) {
            filters.push(`category.id::${categoryId}`);
          }

          if (hasTagFilters) {
            filters.push(`tagsid::${tagId}`);
          }

          if (hasStoryFilters) {
            filters.push(`stories::${storyId}`);
          }
        }

        const limit = getComponentDataFetchLimit({
          componentType: ComponentType.FILTERED_LIST,
          dataType: DataType.ASSETS,
          componentLayout: useVerticalPosters
            ? Layout.VERTICAL
            : Layout.HORIZONTAL,
          viewportType,
        });

        setIsLoading(true);

        // dispatch the actual fetch action so that assets are stored in redux
        // but for the needs of this component keep them in a local state for
        // more precise control
        const {
          _embedded: { assets },
        } = await dispatch(
          fetchSvpAssets({
            // WARNING: adding query parameters to this FAKE url will result in error thrown by the URL constructor
            dataUrl: '/search', // matches the /search endpoint
            limit,
            vendor,
            filter: filters.join('|'),
            // remove html tags to avoid potential xss
            query: hasQuery ? stripTags(query) : undefined,
          })
        ).unwrap();

        // TODO: handle fetching next page by grabbing links.next from the response
        setAssets(
          assets.filter((asset) => !isAssetRestricted(config.provider, asset))
        );
        setIsLoading(false);
      } else {
        setAssets([]);
      }
    },
    [categoryId, dispatch, useVerticalPosters, viewportType, vendor]
  );

  return (
    <FilteredList
      assets={assets.map((asset) => {
        const assetProps = getAssetProps(asset);

        if (useVerticalPosters) {
          delete assetProps.publishedDate;
        }

        return {
          ...assetProps,
          asset,
          labels: getAssetLabels(asset, { ...componentConfig, categoryId }),
          imageSources: getImageSources(
            useVerticalPosters
              ? {
                  imageUrl: getAssetVerticalImage(asset),
                  imagePreset: 'assetsFilteredListVertical',
                  aspectRatio: 'vertical',
                }
              : {
                  imageUrl: getAssetMainImage(asset),
                  imagePreset: 'assetsFilteredList',
                  aspectRatio: 'horizontal',
                }
          ),
        };
      })}
      header={header}
      filters={filters}
      className={classNames(styles.container, {
        [styles.vertical]: useVerticalPosters,
      })}
      onChange={onFiltersChange}
      trackSearch={onTrackSearch}
      searchPlaceholder={placeholder}
      isLoading={isLoading}
      initiallyActiveFilter={initiallyActiveFilter}
      searchInputRef={searchInputRef}
      hideSearchField={hideSearchField}
      pageComponentIndex={pageComponentIndex}
    />
  );
};

export const AssetsFilteredList = memo(AssetsFilteredListBase);
